import { Injectable } from '@angular/core';

interface InheritTicket {
  agency: any | null;
  consolidatorId: string | null;
  consolidator: { id: string; name: string } | null;
  ts: number;
  userId: string;
}

@Injectable({ providedIn: 'root' })
export class TabContextService {
  private static readonly TICKET_KEY = 'tab_inherit_ticket';
  private static readonly TICKET_TTL_MS = 3000;
  private static readonly AGENCY_KEY = 'agency';
  private static readonly CONSOLIDATOR_ID_KEY = 'selectedConsolidatorId';
  private static readonly CONSOLIDATOR_KEY = 'selectedConsolidator';

  getAgency(): any | null {
    const raw = sessionStorage.getItem(TabContextService.AGENCY_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setAgency(agency: any): void {
    sessionStorage.setItem(
      TabContextService.AGENCY_KEY,
      JSON.stringify(agency),
    );
  }

  clearAgency(): void {
    sessionStorage.removeItem(TabContextService.AGENCY_KEY);
  }

  getConsolidatorId(): string {
    return (
      sessionStorage.getItem(TabContextService.CONSOLIDATOR_ID_KEY) || ''
    );
  }

  setConsolidatorId(id: string): void {
    sessionStorage.setItem(TabContextService.CONSOLIDATOR_ID_KEY, id);
  }

  getConsolidator(): { id: string; name: string } | null {
    const raw = sessionStorage.getItem(TabContextService.CONSOLIDATOR_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setConsolidator(consolidator: { id: string; name: string }): void {
    sessionStorage.setItem(
      TabContextService.CONSOLIDATOR_ID_KEY,
      consolidator.id,
    );
    sessionStorage.setItem(
      TabContextService.CONSOLIDATOR_KEY,
      JSON.stringify(consolidator),
    );
  }

  clearConsolidator(): void {
    sessionStorage.removeItem(TabContextService.CONSOLIDATOR_ID_KEY);
    sessionStorage.removeItem(TabContextService.CONSOLIDATOR_KEY);
  }

  clearAll(): void {
    this.clearAgency();
    this.clearConsolidator();
  }

  hydrateFromInheritTicket(userId: string | undefined): void {
    if (
      sessionStorage.getItem(TabContextService.AGENCY_KEY) ||
      sessionStorage.getItem(TabContextService.CONSOLIDATOR_ID_KEY)
    ) {
      return;
    }
    const raw = localStorage.getItem(TabContextService.TICKET_KEY);
    if (!raw) return;
    localStorage.removeItem(TabContextService.TICKET_KEY);
    try {
      const ticket = JSON.parse(raw) as InheritTicket;
      if (!userId || ticket.userId !== userId) return;
      if (Date.now() - ticket.ts > TabContextService.TICKET_TTL_MS) return;
      if (ticket.agency) {
        sessionStorage.setItem(
          TabContextService.AGENCY_KEY,
          JSON.stringify(ticket.agency),
        );
      }
      if (ticket.consolidatorId) {
        sessionStorage.setItem(
          TabContextService.CONSOLIDATOR_ID_KEY,
          ticket.consolidatorId,
        );
      }
      if (ticket.consolidator) {
        sessionStorage.setItem(
          TabContextService.CONSOLIDATOR_KEY,
          JSON.stringify(ticket.consolidator),
        );
      }
    } catch {
      // ignore malformed ticket
    }
  }

  writeInheritTicket(userId: string | undefined): void {
    if (!userId) return;
    const agency = this.getAgency();
    const consolidatorId = this.getConsolidatorId() || null;
    const consolidator = this.getConsolidator();
    if (!agency && !consolidatorId) return;
    const ticket: InheritTicket = {
      agency,
      consolidatorId,
      consolidator,
      ts: Date.now(),
      userId,
    };
    localStorage.setItem(
      TabContextService.TICKET_KEY,
      JSON.stringify(ticket),
    );
  }

  clearInheritTicket(): void {
    localStorage.removeItem(TabContextService.TICKET_KEY);
  }
}
